import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Module } from '../types/module';

interface ModuleContextType {
  modules: Module[];
  registerModule: (module: Module) => void;
  unregisterModule: (moduleId: string) => void;
  getModule: (moduleId: string) => Module | undefined;
}

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

export const useModules = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
};

interface ModuleProviderProps {
  children: ReactNode;
}

export const ModuleProvider: React.FC<ModuleProviderProps> = ({ children }) => {
  const [modules, setModules] = useState<Module[]>([]);

  const registerModule = (module: Module) => {
    setModules(prev => {
      // Prevent duplicate registrations
      if (prev.find(m => m.id === module.id)) {
        console.warn(`Module ${module.id} already registered`);
        return prev;
      }
      const newModules = [...prev, module];
      // Sort by priority if defined
      return newModules.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
    });
  };

  const unregisterModule = (moduleId: string) => {
    setModules(prev => prev.filter(m => m.id !== moduleId));
  };

  const getModule = (moduleId: string) => {
    return modules.find(m => m.id === moduleId);
  };

  return (
    <ModuleContext.Provider value={{ modules, registerModule, unregisterModule, getModule }}>
      {children}
    </ModuleContext.Provider>
  );
};